import axios, { AxiosError, AxiosResponse } from "axios";

import { AnsweredQuestionWithId } from "@teamprevent/common/questionnaires/model";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { ConsultationDetails } from "@teamprevent/common/model";
import { MHPTreatmentReportDto } from "@teamprevent/common/mentalHealthPractitionerTreatment/models";
import { parseConsultationModel } from "@teamprevent/common/consultations/models";

import { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import { AddTaskModel, CreateTaskDto, parseTaskModel, TaskType } from "../task/model";
import { ClinicalNotesAutoSaveDto } from "../clinicalNotes/models";
import { OhCaseDetailModel, parseOhCaseModel } from "../crud/ohCase/model";

import { mapAddTaskModelToCreateTaskDto, mapCreateTaskDtoToServer } from "../crud/ohCase/ohCaseApiMappers";

import { MHPTreatmentConsultation } from "./models";

const apiUrl = "/api/mentalHealthPractitionerTreatment";

const getConsultationNextTaskTypes = async (): Promise<TaskType[] | null> => {
    try {
        const response = await axios.get<TaskType[]>(`${apiUrl}/nextTaskTypes`);
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError && axiosError.response && axiosError.response.status === 404) {
            return null;
        }

        throw ex;
    }
};

const getMHPTreatmentConsultationDetails = async (ohCaseId: string, taskId: string): Promise<ConsultationDetails | null> => {
    try {
        const response = await axios.get<
            ServerModelWithDateStrings<ConsultationDetails>
        >(`${apiUrl}/${ohCaseId}/${taskId}/consultationDetails`);
        return parseConsultationModel(response.data);
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError && axiosError.response && axiosError.response.status === 404) {
            return null;
        }

        throw ex;
    }
};

const loadConsultation = async (ohCaseId: string, taskId: string): Promise<MHPTreatmentConsultation | null> => {
    try {
        const response = await axios.get<MHPTreatmentConsultation>(`${apiUrl}/${ohCaseId}/${taskId}`);
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError && axiosError.response && axiosError.response.status === 404) {
            return null;
        }

        throw ex;
    }
};

const saveConsultation = async (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string): Promise<ClinicalNotesAutoSaveDto> => {
    try {
        return (await axios.post(`${apiUrl}/${ohCaseId}/${taskId}`, { data: { questions }, eTag }))?.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError.response?.status === 400) {
            const error = { message: "Could not save as a newer version exists", status: 400 };
            throw error;
        } else {
            throw ex;
        }
    }
};

const submitConsultation = async (ohCaseId: string, taskId: string, nextTasks: CreateTaskDto[]): Promise<TaskModel[]> => {
    try {
        const response = await axios.post<
            ServerModelWithDateStrings<TaskModel>[],
            AxiosResponse<ServerModelWithDateStrings<TaskModel>[]>,
            ServerModelWithDateStrings<CreateTaskDto>[]
        >(
            `${apiUrl}/${ohCaseId}/${taskId}/complete`,
            nextTasks.map(mapCreateTaskDtoToServer)
        );

        return (response.data).map(parseTaskModel);
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError && axiosError.response && axiosError.response.status === 404) {
            return null;
        }

        throw ex;
    }
};

const getMHPTreatmentConsultationDetailsForReport = async (ohCaseId: string, taskId: string): Promise<ConsultationDetails> => {
    try {
        const response = await axios.get<
            ServerModelWithDateStrings<ConsultationDetails>
        >(`${apiUrl}/report/${ohCaseId}/${taskId}/consultationDetails`);
        return parseConsultationModel(response.data);
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError && axiosError.response && axiosError.response.status === 404) {
            return null;
        }

        throw ex;
    }
};

const loadReport = async (ohCaseId: string, taskId: string): Promise<MHPTreatmentReportDto | null> => {
    try {
        const response = await axios.get<MHPTreatmentReportDto>(`${apiUrl}/report/${ohCaseId}/${taskId}`);
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError && axiosError.response && axiosError.response.status === 404) {
            return null;
        }

        throw ex;
    }
};

const loadReportPreview = async (ohCaseId: string, taskId: string): Promise<MHPTreatmentReportDto | null> => {
    try {
        const response = await axios.get<MHPTreatmentReportDto>(`${apiUrl}/report/${ohCaseId}/${taskId}/preview`);
        return response.data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError && axiosError.response && axiosError.response.status === 404) {
            return null;
        }

        throw ex;
    }
};

const saveReport = async (ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[], eTag: string): Promise<MHPTreatmentReportDto> => {
    try {
        return (await axios.post<MHPTreatmentReportDto>(`${apiUrl}/report/${ohCaseId}/${taskId}`, { data: { questions }, eTag })).data;
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError.response?.status === 400) {
            const error = { message: "Could not save as a newer version exists", status: 400 };
            throw error;
        } else {
            throw ex;
        }
    }
};

const submitReport = async (ohCaseId: string, taskId: string, tasks: AddTaskModel[]): Promise<OhCaseDetailModel | null> => {
    try {
        const createTasks = (tasks ?? []).map(nextTask => mapCreateTaskDtoToServer(mapAddTaskModelToCreateTaskDto(nextTask)));
        const response = await axios.post<
            ServerModelWithDateStrings<OhCaseDetailModel>,
            AxiosResponse<ServerModelWithDateStrings<OhCaseDetailModel>>,
            ServerModelWithDateStrings<CreateTaskDto>[]
        >(`${apiUrl}/report/${ohCaseId}/${taskId}/complete`, createTasks);

        return parseOhCaseModel(response.data);
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError && axiosError.response && axiosError.response.status === 404) {
            return null;
        }

        throw ex;
    }
};

const submitReportAmendment = async (ohCaseId: string, taskId: string, tasks: AddTaskModel[]): Promise<OhCaseDetailModel | null> => {
    try {
        const createTasks = (tasks ?? []).map(nextTask => mapCreateTaskDtoToServer(mapAddTaskModelToCreateTaskDto(nextTask)));
        const response = await axios.post<
            ServerModelWithDateStrings<OhCaseDetailModel>,
            AxiosResponse<ServerModelWithDateStrings<OhCaseDetailModel>>,
            ServerModelWithDateStrings<CreateTaskDto>[]>(`${apiUrl}/report/${ohCaseId}/${taskId}/completeAmendments`, createTasks);

        return parseOhCaseModel(response.data);
    } catch (ex) {
        const axiosError = ex as AxiosError;
        if (axiosError && axiosError.response && axiosError.response.status === 404) {
            return null;
        }

        throw ex;
    }
};

const approveReport = async (ohCaseId: string, taskId: string, notes: string): Promise<OhCaseDetailModel> => {
    const response = await axios.post<ServerModelWithDateStrings<OhCaseDetailModel>>(`${apiUrl}/report/${ohCaseId}/${taskId}/approve`, { notes });

    return parseOhCaseModel(response.data);
};

const rejectReport = async (ohCaseId: string, taskId: string, notes: string): Promise<TaskModel[]> => {
    const response = await axios.post<ServerModelWithDateStrings<TaskModel>[]>(`${apiUrl}/report/${ohCaseId}/${taskId}/reject`, { notes });
    return (response.data).map(parseTaskModel);
};

const requestReportChanges = async (ohCaseId: string, taskId: string, notes: string): Promise<TaskModel[]> => {
    const response = await axios.post<ServerModelWithDateStrings<TaskModel>[]>(`${apiUrl}/report/${ohCaseId}/${taskId}/requestChanges`, { notes });
    return (response.data).map(parseTaskModel);
};

export const mhpTreatmentApi = {
    getConsultationNextTaskTypes,
    getMHPTreatmentConsultationDetails,
    loadConsultation,
    saveConsultation,
    submitConsultation,
    getMHPTreatmentConsultationDetailsForReport,
    loadReport,
    loadReportPreview,
    saveReport,
    submitReport,
    submitReportAmendment,
    approveReport,
    rejectReport,
    requestReportChanges
};
